import { useCallback } from 'react';
import { useApiClient } from './useApiClient';

export interface CreateDecisionPayload {
  rule_id: string;
  scope_type: 'dataset' | 'schema' | 'catalog' | 'global';
  scope_natural_key?: string | null;
  scope_id?: string | null;
  action: 'accept' | 'reject' | 'edit';
  asserted_status: 'PASS' | 'FAIL' | 'NOT_APPLICABLE';
  rationale?: string | null;
  machine_verdict_at_decision_time?: 'PASS' | 'FAIL' | 'NOT_APPLICABLE' | null;
  /**
   * Tier-bearing rules only (F2 today). 0 = below Tier 1; 1, 2, 3 = explicit
   * tier override. Omit for binary-status decisions.
   */
  asserted_maturity_tier?: number | null;
  machine_maturity_tier_at_decision_time?: number | null;
}

export function useFairDecisions() {
  const { authFetch } = useApiClient();

  const createDecision = useCallback(async (payload: CreateDecisionPayload) => {
    const r = await authFetch('/api/fair/decisions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const errBody = await r.text();
      throw new Error(`Failed to create decision (HTTP ${r.status}): ${errBody}`);
    }
    return r.json() as Promise<unknown>;
  }, [authFetch]);

  const supersedeDecision = useCallback(async (
    id: string,
    body: { action?: string; asserted_status?: string; rationale?: string | null },
  ) => {
    const r = await authFetch(`/api/fair/decisions/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error(`Failed to supersede decision (HTTP ${r.status})`);
    return r.json() as Promise<unknown>;
  }, [authFetch]);

  const deleteDecision = useCallback(async (id: string) => {
    const r = await authFetch(`/api/fair/decisions/${id}`, { method: 'DELETE' });
    if (!r.ok) throw new Error(`Failed to delete decision (HTTP ${r.status})`);
  }, [authFetch]);

  return { createDecision, supersedeDecision, deleteDecision };
}
